import { Container, TabList, Tabs, VStack } from "@chakra-ui/react";
import { Outlet, useLocation } from "react-router";

import { dayjs } from "@equidefi/i18n";
import { Tab } from "@equidefi/ui";
import type React from "react";
import { useMemo } from "react";
import { useOfferingContext } from "./OfferingContext";
import { OfferingHeaderCard } from "./components/OfferingHeaderCard";

export function OfferingNavigation(): React.JSX.Element {
  const location = useLocation();
  const { offering } = useOfferingContext();

  const analyticsEnabled = offering?.settings?.analytics_enabled;

  const defaultIndex = useMemo(() => {
    const tabs = ["pre-closing", "closing", "tranches", "payments", "payouts", "profiles", "details"];
    if (analyticsEnabled) tabs.unshift("analytics");

    const index = tabs.filter(Boolean).findIndex((tab) => location.pathname.includes(tab)) + 1;

    return index;
  }, [location, analyticsEnabled]);

  return (
    <Tabs mb={2} variant="equidefi" index={defaultIndex}>
      <TabList>
        <Tab href="./investments">Investments</Tab>
        {analyticsEnabled && <Tab href="./analytics">Analytics</Tab>}
        <Tab href="./pre-closing">Pre-Closing</Tab>
        <Tab href="./closing">Closing</Tab>
        <Tab href="./tranches">Closed Tranches</Tab>
        <Tab href="./payments">Payments</Tab>
        <Tab href="./payouts">Payouts</Tab>
        <Tab href="./profiles">Investors</Tab>
        <Tab href="./details">Offering Details</Tab>
      </TabList>
    </Tabs>
  );
}

export const useOfferingStatusBanner = (_date?: string) => {
  if (!_date)
    return {
      is_passed: false,
      is_nearing: false,
    };

  let is_passed = false;
  let is_nearing = false;

  const [now, date] = [dayjs(), dayjs(_date)];

  const isPassedEndDate = date.isBefore(now);
  const isNearingEndDate = now.isBetween(date.subtract(2, "weeks"), date);

  if (isPassedEndDate) {
    is_passed = true;
  }

  if (isNearingEndDate) {
    is_nearing = true;
  }

  return {
    is_passed,
    is_nearing,
  };
};

export default function OfferingAreaContainer(): React.JSX.Element {
  const { offering } = useOfferingContext();

  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={4} align="stretch">
        <OfferingHeaderCard offering={offering} />
        <OfferingNavigation />
        <Outlet />
      </VStack>
    </Container>
  );
}
